import React, { useEffect, useState, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import { Typography, Stack, CardContent, Card, Box, CircularProgress, Tabs, Tab } from '@mui/material'
import { ClassNameMap, withStyles } from '@mui/styles'
import stylesheet from '../detail-container.stylesheet'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../redux/store'
import Helper from '../../../../utils/helper'
import moment from 'moment'
import { PatientService } from '../../../../services/patient.service'
import { PatientProviderSite } from '../../../../model/provider.types'
import { CareTeamManager } from '../../../care-team'
import { isControlVisible } from '../comp-config'
import StarIcon from '@mui/icons-material/Star'

interface IProps {
  input: any
  classes: ClassNameMap
  editCareTeamOpen?: boolean
  onCloseCareTeamEdit?: () => void
}

const patientService = new PatientService()

const PatientClinicalInfoComponent: React.FC<IProps> = ({ input, classes, editCareTeamOpen = false, onCloseCareTeamEdit }) => {
  const history = useHistory()
  const patientDetail = useSelector((state: RootState) => state.patientDetail.patientDetail)
  const [clinicalData, setClinicalData] = useState({}) as any
  const [careTeam, setCareTeam] = useState<PatientProviderSite[]>([])
  const [careTeamLoading, setCareTeamLoading] = useState(false)
  let [clinicVisit, setClinicVisit] = useState([]) as any

  useEffect(() => {
    if (input.type === "patient-list") {
      setClinicalData(input?.clinical_team)
    } else if (input.type === "clinic-visits") {
      setClinicVisit(patientDetail?.clinic_visit_logs || [])
    } else
      setClinicalData(patientDetail?.clinical_team)
  }, [patientDetail, input])

  // to load care team
  useEffect(() => {
    if (input.type === 'clinical-info' && patientDetail?.id) {
      setCareTeam(patientDetail?.provider_sites || [])
    }
  }, [patientDetail?.id, input.type])

  const updateCareTeam = () => {
    if (input.type === 'clinical-info' && patientDetail?.id) {
      setCareTeamLoading(true)
      patientService.getPatientCareTeam(patientDetail.id)
        .then((res: any) => {
          setCareTeam(res?.data?.care_team || res?.data || [])
        })
        .catch((err: any) => {
          console.error('Failed to load care team:', err)
          setCareTeam([])
        })
        .finally(() => {
          setCareTeamLoading(false)
        })
    }
  }

  const getVal = (title: any, val: any) => {
    return <Box mb={1} className={classes.customMiniCard}>
      <Card variant="outlined" className={classes.compactCard}>
        <CardContent className={classes.cardsContentPadding}>
          <Stack direction="row" alignItems={'center'} justifyContent={'space-between'} className={classes.compactCardContent}>
            <Typography variant="body2" className={classes.desc}>
              {title}
            </Typography>
            <Typography pr={1} align='right' className={classes.desc} variant="body2">
              {val}
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  }

  const renderClinicVisit = () => {
    let currVisit = {} as any
    currVisit = clinicVisit && (clinicVisit?.length > 0) && clinicVisit.at(-1)
    const isFutureDate = () => {
      const vDate = currVisit?.visit_date || ''
      if (!vDate) return false;
      const targetDate = moment.unix(vDate);
      return targetDate.isAfter(moment());
    }

    const getLatestNote = (data: any) => {
      const values = Object.values(data) as any
      const lastObject = values[values.length - 1];
      return lastObject?.text;
    }

    return Object.keys(currVisit)?.length > 0
      ? <Box mb={1} className={classes.customMiniCard}>
        <Card variant="outlined" className={classes.compactCard} sx={{ boxShadow: '0px 1px 1px 1px var(--color-border-light) !important' }}>
          <CardContent className={classes.cardsContentPadding} sx={{ backgroundColor: 'var(--color-paper)' }}>
            <Box className={classes.compactCardContent}>
              <Stack direction={'row'} spacing={0.3} justifyContent={'flex-start'}>
                <Typography variant="body2" className={classes.desc} sx={{ wordBreak: 'break-all' }}>
                  {currVisit?.visit_date
                    ? moment.unix(currVisit?.visit_date).format("MM/DD/YY")
                    : '-'
                  }
                </Typography>
                <Box style={{ marginLeft: 'auto', display: 'flex' }}>
                  <Typography variant="body2" className={classes.desc} pr={1}>
                    {currVisit?.status && !isFutureDate() ? Helper.toCamelCase(currVisit?.status) : '-'}
                  </Typography>
                </Box>
              </Stack>
              <Typography variant="body2" className={classes.desc} align='left' sx={{ wordBreak: 'break-all', paddingRight: '0.5rem' }}>
                {getLatestNote(currVisit?.notes)}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
      : ''
  }

  // Render a single care team member
  const renderCareTeamMember = (member: PatientProviderSite) => {
    const provider = member.provider
    const site = member.site
    const displayName = provider?.display_name ||
      `${provider?.first_name || ''} ${provider?.last_name || ''}`.trim() || 'Unknown'
    const role = member.role ? Helper.toCamelCase(member.role.replace(/_/g, ' ')) : ''
    const specialty = provider?.specialty || provider?.taxonomy_display || ''

    return (
      <Box key={member.id} mb={1} className={classes.customMiniCard}>
        <Card variant="outlined" className={classes.compactCard}>
          <CardContent className={classes.cardsContentPadding}>
            <Stack direction="column" spacing={0.5} className={classes.compactCardContent}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" className={classes.desc} sx={{ fontWeight: 500 }}>
                  {/* Gold star inline before the name, exactly as Preferred Pharmacy marks
                      the preferred store — the flag is legible while scanning a list of
                      members, without opening the care team dialog (RD 2026-08-08). */}
                  {member.is_referring && (
                    <StarIcon sx={{ fontSize: 14, color: 'gold', verticalAlign: 'middle', mr: 0.5 }} />
                  )}
                  {displayName}
                  {provider?.credential && `, ${provider.credential}`}
                </Typography>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {member.is_primary && (
                    <Typography variant="caption" sx={{
                      backgroundColor: 'var(--color-badge-info-bg)',
                      color: 'var(--color-badge-info-text)',
                      padding: '1px 6px',
                      borderRadius: '4px',
                      fontSize: '0.65rem'
                    }}>
                      Primary
                    </Typography>
                  )}
                  {/* Referring is a flag on the membership, so it sits alongside the
                      specialty rather than replacing it. Set via the care team edit
                      dialog, same as Primary and role (RD 2026-08-08). */}
                  {member.is_referring && (
                    <Typography variant="caption" sx={{
                      backgroundColor: 'var(--color-badge-info-bg)',
                      color: 'var(--color-badge-info-text)',
                      padding: '1px 6px',
                      borderRadius: '4px',
                      fontSize: '0.65rem'
                    }}>
                      Referring
                    </Typography>
                  )}
                </Stack>
              </Stack>
              {(role || specialty) && (
                <Typography variant="caption" color="textSecondary" className={classes.desc} sx={{ pr: 1 }}>
                  {role}{role && specialty ? ' • ' : ''}{specialty}
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>
    )
  }

  // State for care team tabs
  const [careTeamTab, setCareTeamTab] = useState(0)

  // Render dynamic care team (for non-AIDA mode)
  const renderDynamicCareTeam = () => {
    // The Associated/Unassociated split only carries information when providers arrive
    // from a Surescripts import as an unattached pile of names. Where every member is
    // entered by hand there is nothing to associate against, so the tabs are noise and
    // the Unassociated tab is permanently empty (RD 2026-08-08).
    const showAssociation = isControlVisible('care_team_association')
    const associatedMembers = careTeam.filter((member: PatientProviderSite) => member.is_validated)
    const unassociatedMembers = careTeam.filter((member: PatientProviderSite) => !member.is_validated)

    return (
      <>
        {careTeamLoading ? (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={20} />
          </Box>
        ) : careTeam.length === 0 ? (
          <Box mb={1} className={classes.customMiniCard}>
            <Card variant="outlined" className={classes.compactCard}>
              <CardContent className={classes.cardsContentPadding}>
                <Typography variant="body2" color="textSecondary" className={classes.desc}>
                  No care team members
                </Typography>
              </CardContent>
            </Card>
          </Box>
        ) : !showAssociation ? (
          <Stack direction="column" spacing={0}>
            {careTeam.map((member: PatientProviderSite) => renderCareTeamMember(member))}
          </Stack>
        ) : (
          <>
            <Tabs
              value={careTeamTab}
              onChange={(_, newValue) => setCareTeamTab(newValue)}
              sx={{
                minHeight: '28px',
                mb: 1,
                '& .MuiTab-root': {
                  minHeight: '28px',
                  py: 0.5,
                  px: 1,
                  fontSize: '0.75rem',
                  textTransform: 'none',
                },
                '& .MuiTabs-indicator': {
                  height: '2px',
                },
              }}
            >
              <Tab label={`Associated (${associatedMembers.length})`} />
              <Tab label={`Unassociated (${unassociatedMembers.length})`} />
            </Tabs>
            <Stack direction="column" spacing={0}>
              {careTeamTab === 0 ? (
                associatedMembers.length > 0 ? (
                  associatedMembers.map((member: PatientProviderSite) => renderCareTeamMember(member))
                ) : (
                  <Typography variant="body2" color="textSecondary" className={classes.desc} sx={{ py: 1 }}>
                    No associated providers
                  </Typography>
                )
              ) : (
                unassociatedMembers.length > 0 ? (
                  unassociatedMembers.map((member: PatientProviderSite) => renderCareTeamMember(member))
                ) : (
                  <Typography variant="body2" color="textSecondary" className={classes.desc} sx={{ py: 1 }}>
                    No unassociated providers
                  </Typography>
                )
              )}
            </Stack>
          </>
        )}

        {/* CareTeamManager Dialog */}
        {patientDetail?.id && (
          <CareTeamManager
            patientId={patientDetail.id}
            open={editCareTeamOpen}
            onClose={() => onCloseCareTeamEdit?.()}
            onUpdate={updateCareTeam}
            onEditProvider={(providerId) => {
              history.push(`/providers?id=${providerId}&patientId=${patientDetail.id}`)
            }}
          />
        )}
      </>
    )
  }

  // Render AIDA-specific hardcoded clinical team
  const renderAIDAClinicalTeam = () => {
    return (
      <Stack direction={'column'} spacing={0}>
        {getVal('Amyloid cardiologist', clinicalData?.amyloid_cardiologist?.display_name || '-')}
        {getVal('Cardiologist', clinicalData?.cardiologist?.display_name || '-')}
        {getVal('Hematologist', clinicalData?.hematologist?.display_name || '-')}
        {getVal('Neurologist', clinicalData?.neurologist?.display_name || '-')}
      </Stack>
    )
  }

  const renderPatientInfo = () => {
    return <Stack direction="column" pt={0.2}>
      {input.type === 'patient-info'
        ? <Stack direction={'column'} spacing={0}>
          {getVal('Confirmed CA diagnosis', patientDetail.confirmed_ca_diagnosis !== null
            ? Helper.toCamelCase(JSON.stringify(patientDetail.confirmed_ca_diagnosis))
            : '-'
          )}
          {isControlVisible('referring_free_text') &&
            getVal('Referring', clinicalData?.referring_provider?.display_name || '-')}
          {getVal('Referral pathway', patientDetail?.referral_pathway || '-')}
          {getVal('Registry status', patientDetail?.registry_status || '-')}
        </Stack>
        : input?.type === 'clinic-visits'
          ? renderClinicVisit()
          : renderDynamicCareTeam()
      }
    </Stack>
  }
  return (<>{renderPatientInfo()}</>)
}

export default withStyles(stylesheet)(PatientClinicalInfoComponent)
